import { useEffect, useRef, type ReactNode } from 'react';
import { Button, RadioButton, RadioButtonGroup } from '@carbon/react';
import { Edit, Locked, TrashCan } from '@carbon/icons-react';
import styles from './spatial-filter-controls.module.scss';

/** One map filter mode, as its radio offers it. */
export interface SpatialFilterMode<TMode extends string> {
  mode: TMode;
  label: string;
}

/** The "AOI Polygon" row under the radios, and what sits below it. */
export interface SpatialFilterAoi {
  /** A polygon is drawn: Edit and Delete act on it. Without one both are disabled. */
  hasPolygon: boolean;
  editLabel: string;
  deleteLabel: string;
  /** Starts vertex editing on the map. */
  onEdit: () => void;
  /** Discards the polygon. */
  onDelete: () => void;
  /** Shows Delete in red, as Map Query's. */
  dangerDelete?: boolean;
  /** Vertex editing is on. With `onDone`, the row offers Done in place of Edit and Delete. */
  editing?: boolean;
  /** Ends vertex editing. Omit to leave editing to end on the map (a click away). */
  onDone?: () => void;
  /** Under the row: the "Polygon drawn" chip, or a drawing hint. */
  children?: ReactNode;
}

export interface SpatialFilterControlsProps<TMode extends string> {
  /** The radio group's name. */
  name: string;
  /** Each radio's id is `${idPrefix}-${mode}`. */
  idPrefix: string;
  legendText: string;
  /** The modes, in display order. */
  modes: readonly SpatialFilterMode<TMode>[];
  mode: TMode;
  onModeChange: (mode: TMode) => void;
  /** Shows the AOI row below the radios. Omit for none. */
  aoi?: SpatialFilterAoi;
}

/**
 * A map filter's mode radios (vertical Carbon `RadioButtonGroup`), and, when the
 * caller asks for it, an "AOI Polygon" row with Edit and Delete for the drawn
 * polygon. Props-driven: the caller owns the mode, the polygon and the editing
 * flag (see `QueryMap`'s `'polygon'` query), and decides when the AOI row shows.
 * Edit swapped for Done, or Done for Edit, by a click or a key on it passes its
 * focus to the button in its place.
 *
 * @param {SpatialFilterControlsProps} props - Modes, the current mode and the AOI row.
 * @return {JSX.Element} The radios and the AOI row.
 */
export function SpatialFilterControls<TMode extends string>({
  name,
  idPrefix,
  legendText,
  modes,
  mode,
  onModeChange,
  aoi,
}: SpatialFilterControlsProps<TMode>) {
  const showDone = Boolean(aoi?.editing && aoi.onDone);
  const editRef = useRef<HTMLButtonElement>(null);
  const doneRef = useRef<HTMLButtonElement>(null);
  // Whether Edit or Done was just pressed, asking for Done (true) or Edit in its
  // place; the pressed button goes, and its focus with it to the page.
  const pressed = useRef<boolean | null>(null);
  useEffect(() => {
    if (pressed.current === showDone) {
      (showDone ? doneRef : editRef).current?.focus();
    }
    pressed.current = null;
  });

  return (
    <>
      <RadioButtonGroup
        name={name}
        legendText={legendText}
        orientation="vertical"
        valueSelected={mode}
        // Carbon types the selected value as `string | number`; narrow it back
        // through the mode list (no cast) so a stray radio value is dropped.
        onChange={(value) => {
          const next = modes.find((entry) => entry.mode === value)?.mode;
          if (next) {
            onModeChange(next);
          }
        }}
      >
        {modes.map(({ mode: value, label }) => (
          <RadioButton
            key={value}
            id={`${idPrefix}-${value}`}
            value={value}
            labelText={label}
          />
        ))}
      </RadioButtonGroup>

      {aoi && (
        <div className={styles.appliedAoi}>
          <div className={styles.appliedAoiHeader}>
            <span className={styles.appliedAoiLabel}>AOI Polygon</span>
            {showDone ? (
              <Button
                ref={doneRef}
                kind="primary"
                size="sm"
                renderIcon={Locked}
                onClick={() => {
                  pressed.current = false;
                  aoi.onDone?.();
                }}
              >
                Done
              </Button>
            ) : (
              <>
                <Button
                  ref={editRef}
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={Edit}
                  iconDescription={aoi.editLabel}
                  tooltipPosition="bottom"
                  disabled={!aoi.hasPolygon}
                  onClick={() => {
                    pressed.current = true;
                    aoi.onEdit();
                  }}
                />
                <Button
                  kind={aoi.dangerDelete ? 'danger--ghost' : 'ghost'}
                  size="sm"
                  hasIconOnly
                  renderIcon={TrashCan}
                  iconDescription={aoi.deleteLabel}
                  tooltipPosition="bottom"
                  disabled={!aoi.hasPolygon}
                  onClick={aoi.onDelete}
                />
              </>
            )}
          </div>
          {aoi.children}
        </div>
      )}
    </>
  );
}
